import { useState, useRef } from 'react';
import { X } from 'lucide-react';
import { Field, FieldGroup, FieldError } from '@/shared/components/ui/field';
import { InputGroup, InputGroupInput } from '@/shared/components/ui/input-group';
import { Badge } from '@/shared/components/ui/badge';
import { useTranslation } from 'react-i18next';

interface EmailInviteInputProps {
  emails: string[];
  onChange: (emails: string[]) => void;
}

function isValidEmail(email: string): boolean {
  return /\S+@\S+\.\S+/.test(email);
}

export function EmailInviteInput({ emails, onChange }: EmailInviteInputProps) {
  const { t } = useTranslation('onboarding');
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const addEmail = (value: string) => {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return;

    if (!isValidEmail(trimmed)) {
      setError(t('invite.errors.invalid'));
      return;
    }

    if (emails.includes(trimmed)) {
      setError(t('invite.errors.duplicate'));
      return;
    }

    onChange([...emails, trimmed]);
    setInputValue('');
    setError(null);
  };

  const removeEmail = (email: string) => {
    onChange(emails.filter((e) => e !== email));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail(inputValue);
    }
    if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      removeEmail(emails[emails.length - 1]);
    }
  };

  const handleBlur = () => {
    if (inputValue) addEmail(inputValue);
  };

  return (
    <FieldGroup>
      <Field data-invalid={!!error}>
        {/* Emails agregados como badges */}
        {emails.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {emails.map((email) => (
              <Badge
                key={email}
                variant="secondary"
                className="flex items-center gap-1.5 px-2.5 py-1 text-sm"
              >
                {email}
                <button
                  type="button"
                  data-testid="onboarding-email-remove"
                  onClick={() => removeEmail(email)}
                  className="hover:text-destructive transition-colors"
                  aria-label={t('invite.remove', { email })}
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {/* Input */}
        <InputGroup>
          <InputGroupInput
            ref={inputRef}
            type="email"
            data-testid="onboarding-email-input"
            value={inputValue}
            placeholder={t('invite.placeholder')}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            aria-invalid={!!error}
          />
        </InputGroup>

        {error && <FieldError errors={[{ message: error }]} />}
      </Field>

      <p className="text-xs text-muted-foreground">{t('invite.hint')}</p>
    </FieldGroup>
  );
}
